import React, { useState } from 'react';

import { MESSAGE_MODALS } from '../../context/custom/MessageModalContext';
import { ALERT_TYPE, MASK_FORMAT, REQUEST_METHOD, ShowToastMessage } from '@laramie/ui';
import { APIS } from '../../config/apis';
import { useForm, useLista, useManagedContext, useNav } from '../../components/hooks';
import { ServerRequest } from '../../utils/apiweb';
import { Loading, TableCustom, SectionHeading, AdvancedSearch, InputFormat, InputWithEnterEvent } from '../../components/common';
import DataTaggerModalApi from '../../components/controls/DataTaggerModalApi';
import {usePermissions} from "../../components/hooks/usePermissions";
import {PERMISSIONS} from "../../consts/permissions";
import { TIPS } from '../../consts/tips';
import { VALIDATION_MESSAGE } from '../../consts/validationMessage';
import { BUSINESS_CODE } from '../../consts/businessCode';

function PersonasJuridicasView() {

    //hooks
    const navigate = useNav()
    const { showMessageModal } = useManagedContext()
    const [tableVisible, setTableVisible] = useState(false)
    const [searchMessage, setSearchMessage] = useState(null)

    const [state, setState] = useState({
        loading: false,
        showForm: false,
        dataTagger: {
            showModal: false,
            idEntidad: null
        },
        list: []
    });

    const [filters, setFilters] = useState({
        labels: [
            { title: 'Documento', field: 'numeroDocumento', value: '', valueIgnore: ''},
            { title: 'Denominación/Nombre comercial', field: 'nombre', value: '', valueIgnore: '' },
            { title: 'Etiqueta', field: 'etiqueta', value: '', valueIgnore: '' }
        ]
    });

    const [ formValues, formHandle, , formSet] = useForm({
        numeroDocumento: '',
        nombre: '',
        etiqueta: ''
    });

    const [, getRowLista] = useLista({
        listas: ['TipoDocumento','FormaJuridica'],
        onLoaded: (listas, isSuccess, error) => {
          if (isSuccess) {
            setState(prevState => ({...prevState}));
          }
          else {
            ShowToastMessage(ALERT_TYPE.ALERT_ERROR, error);
          }
        },
        memo: {
          key: 'TipoDocumento_FormaJuridica',
          timeout: 0
        }
    });  
    
    const getDescTipoDocumento = (id) => {
        const row = getRowLista('TipoDocumento', id);
        return (row) ? row.nombre : '';
    }     

    const getDescFormaJuridica = (id) => {
        const row = getRowLista('FormaJuridica', id);
        return (row) ? row.nombre : '';
    }

    const { hasPermission } = usePermissions();
    const canEdit = hasPermission(PERMISSIONS.PERSONA_JURIDICA_EDIT);

    //definiciones
    const tableColumns = [
        { Header: 'Documento', Cell: (props) => {
            const row = props.row.original;
            const tipoDocumento = getDescTipoDocumento(row.idTipoDocumento);
            return `${tipoDocumento} ${row.numeroDocumento}`;
          }, id: 'documento', accessor: 'documento', width: '25%' },           
        { Header: 'Denominación', accessor: 'denominacion', width: '30%' },
        { Header: 'Forma jurídica', Cell: (props) => getDescFormaJuridica(props.value), accessor: 'idFormaJuridica', width: '35%' },
    ];

    

    //handles
    const avmr = {
        onAdd: () => {
            navigate({ to: `/persona-juridica/new` })
        },
        onView: ({ id }) => {
            navigate({ to: `/persona-juridica/view/${id}` })
        },
        onModify: ({ id }) => {
            navigate({ to: `/persona-juridica/edit/${id}` })
        },
        // Comentado hasta que se defina qué hacer con la eliminación de personas.
        /*onRemove: ({ id }) => {
            const persona = state.list.find(x => x.id === id)
            showMessageModal({
                ...MESSAGE_MODALS.BORRAR,
                message: `¿Está seguro de borrar la persona jurídica ${persona.denominacion}?`,
                onConfirm: () => RemovePersonaJuridica(id),
            })
        },*/
        onAdditionalInfo: ({ id }, disabled) => {
            setState(prevState => ({...prevState, dataTagger: {showModal: true, idEntidad: id, disabled}}))
        },
    }

    //callbacks
    const callbackNoSuccess = (response) => {
        response.json()
        .then((error) => {
          if (error.businessCode === BUSINESS_CODE.MAX_QUERY_SIZE_EXCEEDED) {
            setState(prevState => {
              return {...prevState, loading: false, list: []};
            });
            setTableVisible(false);
            setSearchMessage(VALIDATION_MESSAGE.TOO_MANY_RESULTS);
            return;
          }
          const message = error.message;
          ShowToastMessage(ALERT_TYPE.ALERT_ERROR, message);
          setState(prevState => {
            return {...prevState, loading: false};
          });
        })
        .catch((error) => {
            const message = 'Error procesando respuesta: ' + error;
            ShowToastMessage(ALERT_TYPE.ALERT_ERROR, message);
            setState(prevState => {
              return {...prevState, loading: false};
            });
        });
    }
    const callbackError = (error) => {
        const message = 'Error procesando solicitud: ' + error.message;
        ShowToastMessage(ALERT_TYPE.ALERT_ERROR, message);
        setState(prevState => {
          return {...prevState, loading: false};
        });
    }      

    //funciones
    function SearchPersonasJuridicas() {

        let resolveSearch;
        const searchDone = new Promise((resolve) => { resolveSearch = resolve; });

        setState(prevState => {
            return {...prevState, loading: true, list: []};
        });
        setSearchMessage(null);

        const callbackSuccess = (response) => {
            response.json()
            .then((data) => {
                data.forEach(x => {
                    if (x.fechaConstitucion) x.fechaConstitucion = new Date(x.fechaConstitucion);
                });
                setState(prevState => {
                    return {...prevState, loading: false, list: data};
                });
                setTableVisible(true);
                resolveSearch(true);
            })
            .catch((error) => {
                const message = 'Error procesando respuesta: ' + error;
                ShowToastMessage(ALERT_TYPE.ALERT_ERROR, message);
                setState(prevState => {
                    return {...prevState, loading: false};
                });
                resolveSearch(false);
            });
        };

        const paramsUrl = `/filter?`+
        `numeroDocumento=${encodeURIComponent(formValues.numeroDocumento)}&`+
        `nombre=${encodeURIComponent(formValues.nombre)}&`+
        `etiqueta=${encodeURIComponent(formValues.etiqueta)}`;
        
        ServerRequest(
            REQUEST_METHOD.GET,
            null,
            true,
            APIS.URLS.PERSONA_JURIDICA,
            paramsUrl,
            null,
            callbackSuccess,
            (response) => {
                callbackNoSuccess(response);
                resolveSearch(false);
            },
            (error) => {
                callbackError(error);
                resolveSearch(false);
            }
        );

        return searchDone;
    }
    
    // Comentado hasta que se defina qué hacer con la eliminación de personas.
    /*function RemovePersonaJuridica(id) {

        setState(prevState => {
            return {...prevState, loading: true};
        });

        const callbackSuccess = (response) => {
            response.json()
            .then((id) => {
                setState(prevState => {
                    return {...prevState, loading: false};
                });
                SearchPersonasJuridicas();
            })
            .catch((error) => {
                const message = 'Error procesando respuesta: ' + error;
                ShowToastMessage(ALERT_TYPE.ALERT_ERROR, message);
                setState(prevState => {
                    return {...prevState, loading: false};
                });
            });
        };

        const paramsUrl = `/${id}`;

        ServerRequest(
            REQUEST_METHOD.DELETE,
            null,
            true,
            APIS.URLS.PERSONA_JURIDICA,
            paramsUrl,
            null,
            callbackSuccess,
            callbackNoSuccess,
            callbackError
        );        
    } */


    const hasActiveFilters = Object.values(formValues).some(value => String(value ?? '').trim() !== '')

    function ApplyFilters() {
        UpdateFilters();
        if (!hasActiveFilters) {
            setState(prevState => {
                return {...prevState, list: []};
            });
            setTableVisible(false);
            setSearchMessage(VALIDATION_MESSAGE.REQUIRE_FILTER);
            return Promise.resolve(false);
        }
        return SearchPersonasJuridicas();
    }
    function UpdateFilters() {
        let labels = [...filters.labels];
        labels.forEach((label) => {
            label.value = formValues[label.field];
        });
        setFilters(prevState => {
            return {...prevState, labels: labels};
        });
    }


    const dataTaggerRegistro = state.list.find(x => x.id === state.dataTagger.idEntidad)?.denominacion ?? ''

    return (
    <>

        <Loading visible={state.loading}></Loading>

        {state.dataTagger.showModal &&
            <DataTaggerModalApi
                title="Información adicional de persona jurídica"
                registro={dataTaggerRegistro}
                entidad="PersonaJuridica"
                idEntidad={state.dataTagger.idEntidad}
                disabled={state.dataTagger.disabled}
                tipName={TIPS.NAME.MODALS.INFORMACION_ADICIONAL_PERSONA_JURIDICA_MODAL}
                onConfirm={() => {
                    setState(prevState => {
                        return {...prevState, dataTagger: {showModal: false, idEntidad: null}};
                    });
                }}
                onDismiss={() => {
                    setState(prevState => {
                        return {...prevState, dataTagger: {showModal: false, idEntidad: null}};
                    });
                }}
            />
        }

        <SectionHeading titles={['Personas', 'Jurídicas']}
                        onAddClick={canEdit ? avmr.onAdd : null} tableVisible={tableVisible} />

        <section className='section-accordion'>
            
            <AdvancedSearch
                formValues={formValues}
                formSet={formSet}
                labels={filters.labels.filter(f => f.value !== f.valueIgnore)}
                onSearch={ApplyFilters}
                message={searchMessage}
            >

                <div className='row form-basic'>
                    <div className="col-12 col-md-6 col-lg-4">
                        <label htmlFor="numeroDocumento" className="form-label">Número de documento</label>
                        <InputWithEnterEvent
                            useInputFormat={true}
                            name="numeroDocumento"
                            placeholder=""
                            className="form-control"
                            mask={MASK_FORMAT.DOCUMENTO}
                            maskPlaceholder={null}
                            value={ formValues.numeroDocumento }
                            onChange={ formHandle }
                            onEnter={ApplyFilters}
                        />
                    </div>
                    <div className="col-12 col-md-6 col-lg-4">
                        <label htmlFor="nombre" className="form-label">Denominación/Nombre comercial</label>
                        <InputWithEnterEvent
                            name="nombre"
                            type="text"
                            placeholder=""
                            className="form-control"
                            value={ formValues.nombre }
                            onChange={ formHandle }
                            onEnter={ApplyFilters}
                        />
                    </div>
                    <div className="col-12 col-md-6 col-lg-4">
                        <label htmlFor="etiqueta" className="form-label">Etiqueta</label>
                        <InputWithEnterEvent
                            name="etiqueta"
                            type="text"
                            placeholder=""
                            className="form-control"
                            value={ formValues.etiqueta }
                            onChange={ formHandle }
                            onEnter={ApplyFilters}
                        />
                    </div>
                </div>

            </AdvancedSearch>

            {tableVisible && (
                <TableCustom
                    className={'TableCustomBase m-top-20'}
                    columns={tableColumns}
                    data={state.list}
                    avmr={avmr}
                    editCode={PERMISSIONS.PERSONA_JURIDICA_EDIT}
                    messageEmpty="No se encontraron resultados"
                />
            )}

        </section>
        
    </>
    )
}

export default PersonasJuridicasView;
